/**
 * Column triggers — Zod schemas (Milestone 2.4, spec §3.13).
 *
 * Mirrors chaos.column_triggers. Each row pairs an event (fired by the
 * board on card transitions) with one of seven actions. Each action has
 * its own `config` shape — modeled here as a Zod discriminated union on
 * `action`. The runtime executor (not in this milestone) reads `config`
 * per action.
 *
 * DB CHECK constraints (see 0004_m2_boards_cards_post.sql, widened by
 * 0128_column_trigger_watcher_actions.sql):
 *   - event  IN ('card_entered','card_exited','sub_task_completed','card_assigned')
 *   - action IN ('webhook','notify_role','notify_user','spawn_card',
 *                'add_member','run_instruction','set_field',
 *                'run_watcher','surface_lessons')
 *
 * THIS ENUM IS THE REAL GATE, not the DB CHECK. The create/update routes parse
 * against it before anything reaches Postgres, so an action the migration allows
 * but this file omits is unreachable — which is exactly where 'run_watcher' and
 * 'surface_lessons' sat between the Wave-3 executor landing and 2026-07-19.
 */
import { z } from 'zod';

// ─────────────────────────────────────────────────────────────────────
// Event + action enums (must match DB CHECK constraints)
// ─────────────────────────────────────────────────────────────────────
export const columnTriggerEvents = [
  'card_entered',
  'card_exited',
  // CONNECTED 2026-08-07. Declared by 0004, offered by the dialog, and
  // dispatched by NOBODY until sub-tasks.ts started calling
  // fireCardEventTriggers() on both paths that flip a sub-task to done. A user
  // could configure this event and watch it never fire.
  'sub_task_completed',
  // 'card_assigned' WAS HERE AND IS RETIRED BY MIGRATION 0285.
  // Not a missing call: a card is assigned from four scattered places, and one
  // of them is the trigger executor's own `set_field` action, which writes
  // chaos.cards.role_id. Dispatching from there would make triggers fire
  // triggers, and trigger-executor.ts has no loop guard anywhere in it.
  // Re-widening the CHECK is exactly where "did you add a loop guard?" gets
  // asked, which is why the value is gone from the dictionary rather than left
  // in it looking configured.
] as const;
export const columnTriggerEventSchema = z.enum(columnTriggerEvents);
export type ColumnTriggerEvent = z.infer<typeof columnTriggerEventSchema>;

export const columnTriggerActions = [
  'webhook',
  'notify_role',
  'notify_user',
  'spawn_card',
  'add_member',
  'run_instruction',
  'set_field',
  // WAVE 3 (2026-07-19). Migration 0128 widened the DB CHECK and
  // trigger-executor.ts dispatches both — but this enum is what the CREATE and
  // PATCH routes validate against, so until they were listed here NO ROW
  // CARRYING THEM COULD BE CREATED and both handlers were unreachable code.
  'run_watcher',
  'surface_lessons',
] as const;
export const columnTriggerActionSchema = z.enum(columnTriggerActions);
export type ColumnTriggerAction = z.infer<typeof columnTriggerActionSchema>;

// ─────────────────────────────────────────────────────────────────────
// Action-specific config shapes
// ─────────────────────────────────────────────────────────────────────
const httpHeaderSchema = z.record(z.string());

export const webhookConfigSchema = z.object({
  url: z.string().url(),
  method: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE']).optional(),
  headers: httpHeaderSchema.optional(),
  bodyTemplate: z.string().max(50_000).optional(),
});
export type WebhookConfig = z.infer<typeof webhookConfigSchema>;

export const notifyRoleConfigSchema = z.object({
  roleId: z.string().uuid(),
  message: z.string().min(1).max(10_000).optional(),
  messageTemplate: z.string().max(50_000).optional(),
  urgency: z.enum(['low', 'normal', 'high']).optional(),
});
export type NotifyRoleConfig = z.infer<typeof notifyRoleConfigSchema>;

export const notifyUserConfigSchema = z.object({
  userId: z.string().uuid(),
  message: z.string().min(1).max(10_000).optional(),
  messageTemplate: z.string().max(50_000).optional(),
  urgency: z.enum(['low', 'normal', 'high']).optional(),
});
export type NotifyUserConfig = z.infer<typeof notifyUserConfigSchema>;

export const spawnCardConfigSchema = z.object({
  boardId: z.string().uuid(),
  columnId: z.string().uuid(),
  roleId: z.string().uuid().optional(),
  titleTemplate: z.string().min(1).max(2000),
  promptTemplate: z.string().max(200_000).optional(),
});
export type SpawnCardConfig = z.infer<typeof spawnCardConfigSchema>;

export const addMemberConfigSchema = z
  .object({
    memberKind: z.enum(['user', 'role']),
    userId: z.string().uuid().optional(),
    roleId: z.string().uuid().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.memberKind === 'user' && !val.userId) {
      ctx.addIssue({
        code: 'custom',
        message: 'userId required when memberKind=user',
        path: ['userId'],
      });
    }
    if (val.memberKind === 'role' && !val.roleId) {
      ctx.addIssue({
        code: 'custom',
        message: 'roleId required when memberKind=role',
        path: ['roleId'],
      });
    }
  });
export type AddMemberConfig = z.infer<typeof addMemberConfigSchema>;

export const runInstructionConfigSchema = z.object({
  target: z.enum(['current_session', 'spawn_session']),
  instructionText: z.string().min(1).max(200_000),
});
export type RunInstructionConfig = z.infer<typeof runInstructionConfigSchema>;

export const setFieldConfigSchema = z.object({
  field: z.string().min(1).max(120),
  value: z.unknown(),
});
export type SetFieldConfig = z.infer<typeof setFieldConfigSchema>;

/* ────────────────────────────────────────────────────────────────────
 * KEY CASING -- the rule, and the one exception below it.
 *
 * RULE: the seven schemas ABOVE are camelCase, and camelCase is what
 * apps/chaos-gateway/src/trigger-executor.ts reads. That was NOT true until
 * 2026-07-19: the schemas wrote `roleId`/`titleTemplate`/`memberKind` while the
 * executor read `cfg.role_id`/`cfg.title_template`/`cfg.member_kind`, and the
 * create route persists `body.config` verbatim (`config: body.config as any`),
 * so nothing translated. Every one of those actions was dead on arrival -- the
 * executor read `undefined` and logged a skip. It went unnoticed because
 * chaos.column_triggers held ZERO rows.
 *
 * The READERS moved, not these schemas: camelCase is what the rest of the API
 * surface, the ColumnTrigger wire type below (`orgId`, `columnId`, `createdAt`)
 * and the dialog that submits through here already use. The executor tolerates
 * the old snake_case spelling on READ for one release -- see the `cfgGet`
 * docblock in trigger-executor.ts for the query that says when that can go.
 *
 * EXCEPTION: the two Wave-3 schemas BELOW are snake_case, on BOTH sides. That
 * is not a style slip and must not be "aligned" with the rule above -- they are
 * the two that were always correct, apps/chaos-gateway/test/
 * quality-loop-contract.test.ts pins their keys against the executor, and
 * changing them breaks a shipped feature.
 *
 * WHICHEVER side you touch: apps/chaos-gateway/test/
 * column-trigger-config-contract.test.ts asserts, for all nine actions, that
 * every key a schema here accepts is a key the executor actually reads. Add a
 * key here without a reader and it goes red.
 * ──────────────────────────────────────────────────────────────────── */

/** One criterion of the COLUMN's bar. Falls back to the card's own published
 *  criteria when the list is absent or empty (runColumnAudit). */
export const columnAuditCriterionSchema = z.object({
  key: z.string().min(1).max(120).optional(),
  text: z.string().min(1).max(4000),
  required: z.boolean().optional(),
  notes: z.string().max(4000).optional(),
});

/**
 * ADR-025 — the column auditor. Every field is optional: with none of them set,
 * the audit judges the card's own published criteria with the opposite-vendor
 * default and the one-hour cooldown. A judge run has a MEASURED ~80k-token
 * floor and this fires per card landing, so `cooldown_sec` is a money control,
 * not a nicety — its default lives in COLUMN_AUDIT_DEFAULT_COOLDOWN_SEC.
 */
export const runWatcherConfigSchema = z.object({
  criteria: z.array(columnAuditCriterionSchema).max(25).optional(),
  watcher_vendor: z.enum(['anthropic', 'openai', 'google']).optional(),
  watcher_model: z.string().max(200).optional(),
  token_cap: z.number().int().positive().optional(),
  timeout_sec: z.number().int().positive().optional(),
  cooldown_sec: z.number().int().min(0).optional(),
});
export type RunWatcherConfig = z.infer<typeof runWatcherConfigSchema>;

/**
 * Cross-card learning. `write_card_instructions` is opt-in and defaults to
 * false because chaos.card_instructions is profile-bundle layer 08 — the
 * channel that actually reaches the agent — and it holds text a human wrote.
 */
export const surfaceLessonsConfigSchema = z.object({
  max_lessons: z.number().int().positive().max(20).optional(),
  lookback_days: z.number().int().positive().max(365).optional(),
  write_card_instructions: z.boolean().optional(),
});
export type SurfaceLessonsConfig = z.infer<typeof surfaceLessonsConfigSchema>;

/**
 * Discriminated union on `action`. The shape of `config` must match the
 * action; this is enforced at the Zod layer for create/update.
 */
export const columnTriggerActionConfigSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('webhook'), config: webhookConfigSchema }),
  z.object({ action: z.literal('notify_role'), config: notifyRoleConfigSchema }),
  z.object({ action: z.literal('notify_user'), config: notifyUserConfigSchema }),
  z.object({ action: z.literal('spawn_card'), config: spawnCardConfigSchema }),
  z.object({ action: z.literal('add_member'), config: addMemberConfigSchema }),
  z.object({ action: z.literal('run_instruction'), config: runInstructionConfigSchema }),
  z.object({ action: z.literal('set_field'), config: setFieldConfigSchema }),
  z.object({ action: z.literal('run_watcher'), config: runWatcherConfigSchema }),
  z.object({ action: z.literal('surface_lessons'), config: surfaceLessonsConfigSchema }),
]);
export type ColumnTriggerActionConfig = z.infer<typeof columnTriggerActionConfigSchema>;

// ─────────────────────────────────────────────────────────────────────
// Persisted shape + create/update bodies
// ─────────────────────────────────────────────────────────────────────
export const columnTriggerSchema = z.object({
  id: z.string().uuid(),
  orgId: z.string().uuid(),
  columnId: z.string().uuid(),
  event: columnTriggerEventSchema,
  action: columnTriggerActionSchema,
  config: z.unknown(),
  enabled: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type ColumnTrigger = z.infer<typeof columnTriggerSchema>;

/**
 * Body shape: { event, action, config, enabled? }. We validate event +
 * (action, config) shape via the discriminated union, then merge with
 * the event field.
 */
export const createColumnTriggerSchema = z.intersection(
  z.object({
    event: columnTriggerEventSchema,
    enabled: z.boolean().optional(),
  }),
  columnTriggerActionConfigSchema,
);
export type CreateColumnTriggerInput = z.infer<typeof createColumnTriggerSchema>;

/**
 * Update body: any of event / enabled may be patched. If action changes,
 * config must be supplied (validated together via discriminated union on
 * action). enabled-only patch is allowed by sending just { enabled }.
 */
export const updateColumnTriggerSchema = z.union([
  // Patch action + config together (enforces matching shape).
  z.intersection(
    z.object({
      event: columnTriggerEventSchema.optional(),
      enabled: z.boolean().optional(),
    }),
    columnTriggerActionConfigSchema,
  ),
  // Or patch only event / enabled (action+config unchanged).
  z
    .object({
      event: columnTriggerEventSchema.optional(),
      enabled: z.boolean().optional(),
    })
    .refine((v) => v.event !== undefined || v.enabled !== undefined, {
      message: 'at least one of event/enabled/action+config must be provided',
    }),
]);
export type UpdateColumnTriggerInput = z.infer<typeof updateColumnTriggerSchema>;
